// Commitment detail modal — Commitments-Modal-01.
//
// Renders the full Commitment detail surface as a near-fullscreen
// modal overlay (ClickUp proportions: ~90vw × 90vh, centered, with
// a visible margin / dimmed background). Lives at the same
// ``/dashboard/commitments/:commitmentId`` route as the full-page
// view; whether it mounts as a modal or as the full page is
// determined by ``location.state.backgroundLocation`` — set by the
// surface that opened the link (Tracker row, Kanban card, Account
// commitments panel, etc.). Direct URL load / refresh hits the
// full-page route in ``CommitmentDetailPage.tsx`` instead, so deep
// links remain bookmarkable.
//
// Close behavior matches the existing design-system Modal:
//   • clicking the X icon
//   • pressing Escape
//   • clicking the backdrop (outside the dialog)
// On close the modal navigates back via ``navigate(-1)`` when a
// background location is present (preserves filters / scroll on
// the originating page), or falls back to the Commitment Tracker
// route otherwise. Browser-back also closes the modal naturally —
// the modal route is its own history entry, and popping it lands
// on the previous URL.
//
// The modal renders the same ``CommitmentDetailContent`` body the
// full-page route uses, so all detail features (status / priority
// / assignment / comments / attachments / dependencies / private
// rules / playbook source context / blocked / overdue signals)
// keep working without duplication.

import { X } from "lucide-react";
import { useCallback, useEffect, type MouseEvent } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";

import { colors } from "@/design-system/tokens";
import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";

import { CommitmentDetailContent } from "./CommitmentDetailPage";
import { useCommitmentDetailQuery } from "./hooks";
import { CommitmentNotFoundError } from "./types";


// Same backdrop tone the design-system Modal uses (Ink @ 40%).
function inkAtAlpha(alpha: number): string {
  const hex = colors.textPrimary.replace("#", "");
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}


export interface CommitmentDetailModalProps {
  /** Override the close redirect when no background location is
   * available. Defaults to the Commitment Tracker. */
  fallbackUrl?: string;
}


/** Route-overlay modal for ``/dashboard/commitments/:commitmentId``.
 * Reads ``commitmentId`` from the URL and renders the same body
 * the full-page route uses. */
export function CommitmentDetailModal({
  fallbackUrl = "/dashboard/commitment-tracker",
}: CommitmentDetailModalProps) {
  const params = useParams();
  const commitmentId = params.commitmentId ?? "";
  const navigate = useNavigate();

  // Closing the modal: prefer popping history so the originating
  // surface (Tracker, Account, Kanban) restores its filters /
  // scroll exactly. ``window.history.length > 1`` is the safest
  // proxy here — react-router doesn't expose a
  // "do I have a parent location?" hook directly. When history
  // is empty (refresh-into-modal-route, embedded test harness),
  // fall through to a sensible fallback URL.
  const handleClose = useCallback(() => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(fallbackUrl, { replace: true });
  }, [navigate, fallbackUrl]);

  const detailQuery = useCommitmentDetailQuery(commitmentId);

  // Escape closes — only attach while the modal is mounted so we
  // don't fight other ESC consumers when this route isn't active.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") handleClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [handleClose]);

  if (typeof document === "undefined") return null;

  // Click bubbling: only the backdrop should call ``handleClose``;
  // any click landing on the dialog body must not propagate.
  const onDialogClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  let body: React.ReactNode;
  if (detailQuery.isLoading || detailQuery.isPending) {
    body = (
      <div className="p-6">
        <DashboardLoadingState variant="card" />
      </div>
    );
  } else if (detailQuery.isError) {
    body = detailQuery.error instanceof CommitmentNotFoundError ? (
      <div data-testid="commitment-detail-modal-not-found" className="p-6">
        <DashboardEmptyState
          title="Commitment not found"
          description="It may have been deleted, or the URL is wrong."
          action={
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90"
            >
              Back to Commitment Tracker
            </button>
          }
        />
      </div>
    ) : (
      <div data-testid="commitment-detail-modal-error" className="p-6">
        <DashboardErrorState
          title="Couldn't load this Commitment"
          description="Refresh to retry, or check the network connection."
          retry={() => detailQuery.refetch()}
        />
      </div>
    );
  } else if (!detailQuery.data) {
    body = (
      <div className="p-6">
        <DashboardLoadingState variant="card" />
      </div>
    );
  } else {
    body = (
      <div className="px-6 py-5">
        <CommitmentDetailContent
          detail={detailQuery.data}
          chrome="modal"
          onDeleted={handleClose}
        />
      </div>
    );
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="commitment-detail-modal-title"
      data-testid="commitment-detail-modal"
      // Near-fullscreen overlay. The outer flex centers the dialog
      // inside the viewport; ``p-4 sm:p-6`` keeps a visible margin
      // around the edges so the dimmed backdrop reads at a glance
      // and click-out close has a clear target. Below ``sm`` the
      // dialog stretches edge to edge (full-screen modal).
      className="fixed inset-0 z-50 flex items-stretch justify-center p-0 sm:items-center sm:p-4 lg:p-6"
      onClick={handleClose}
      style={{ backgroundColor: inkAtAlpha(0.4) }}
    >
      <div
        // ClickUp proportions: take up almost the entire viewport
        // while keeping a visible ring of dimmed canvas around the
        // edges. ``sm:max-w-[1280px]`` caps width on huge monitors
        // so the inline-edit fields stay scannable; ``h-full
        // sm:h-[calc(100vh-3rem)] lg:h-[calc(100vh-3rem)]`` tracks
        // the outer padding so the modal never bleeds past the
        // viewport. ``min-h-0`` + ``flex-col`` lets the body scroll
        // while the header stays pinned.
        onClick={onDialogClick}
        className="flex h-full w-full min-h-0 flex-col overflow-hidden rounded-none border border-border-subtle bg-raised shadow-overlay sm:h-[calc(100vh-3rem)] sm:max-w-[1280px] sm:rounded-lg lg:h-[calc(100vh-3rem)]"
      >
        <header className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-border-subtle px-5 py-3">
          <h2
            id="commitment-detail-modal-title"
            className="text-md font-semibold text-text-primary"
          >
            {detailQuery.data?.commitment_number
              ? `Commitment ${detailQuery.data.commitment_number}`
              : "Commitment"}
          </h2>
          <button
            type="button"
            data-testid="commitment-detail-modal-close"
            onClick={handleClose}
            aria-label="Close Commitment"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-tertiary transition-fast ease-out hover:bg-recessed hover:text-text-primary"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </header>
        <div
          data-testid="commitment-detail-modal-body"
          // ``min-h-0`` lets ``flex-1`` actually shrink and the
          // overflow-y-auto take effect. Padding lives inside the
          // body slots above so we don't double-pad.
          className="min-h-0 flex-1 overflow-y-auto"
        >
          {body}
        </div>
      </div>
    </div>,
    document.body,
  );
}
